import { z } from "zod";

// ⛔ `||` ET NON `??`.
//
// `??` ne se replie que sur null/undefined. Une VITE_API_URL declaree mais VIDE
// — ce que produit un `ENV VITE_API_URL=` dans un Dockerfile, ou une variable
// non renseignee dans un tableau de bord d'hebergeur — est une chaine vide.
// `?? ` la retenait comme une valeur valable : toutes les URL perdaient alors
// leur prefixe et `/api/auth/staff` devenait `/auth/staff`. nginx ne les
// reconnaissait plus comme des appels d'API, les faisait retomber sur le repli
// SPA, et un POST vers un fichier statique rend 405 Method Not Allowed.
//
// Une variable vide est une ABSENCE, pas un choix.
const BASE = import.meta.env.VITE_API_URL || "/api";

export class ErreurApi extends Error {
  readonly statut: number;
  /** Motif métier rendu par le back : un refus DOIT porter ses avertissements. */
  readonly motif?: string;

  constructor(statut: number, message: string, motif?: string) {
    super(message);
    this.statut = statut;
    this.motif = motif;
  }
}

let jetonCourant: string | null = null;
export const poserJeton = (j: string | null) => {
  jetonCourant = j;
};

/**
 * Le jeton courant, pour les rares appels qui ne passent pas par `appeler` —
 * un envoi `FormData`, où poser soi-même `Content-Type` casserait la frontière
 * multipart que le navigateur génère.
 *
 * ⛔ Existe pour que PERSONNE ne relise `sessionStorage` à la main. Un écran le
 * faisait : il connaissait alors le nom de la clé de stockage et la forme de
 * l'objet sauvé. Changer l'un ou l'autre cassait un dépôt de fichiers, très
 * loin de là où la modification avait eu lieu.
 */
export const jetonActuel = () => jetonCourant;

/**
 * ⛔ QUE FAIRE QUAND LA SESSION EXPIRE PENDANT QU'ON S'EN SERT.
 *
 * La session n'était vérifiée qu'au CHARGEMENT de la page. Passé huit heures,
 * l'onglet resté ouvert continuait d'appeler l'API avec un jeton périmé : chaque
 * écran affichait une erreur, et l'utilisateur — qui n'a aucune raison de penser
 * « session expirée » devant un message d'erreur — croyait l'application en
 * panne. Recharger était le seul remède, et rien ne le suggérait.
 *
 * Un 401 rendu à un porteur de jeton ne veut dire qu'une chose : ce jeton ne
 * vaut plus rien. On efface la session, et le garde de routage renvoie
 * l'utilisateur sur SON écran de connexion.
 */
let surExpiration: (() => void) | null = null;
export const poserSurExpiration = (f: (() => void) | null) => {
  surExpiration = f;
};

/**
 * Appel typé. Le schéma zod est le GARDIEN DE CONTRAT côté front (§10).
 *
 * La panne « Balance importé (undefined lignes) » venait d'un front lisant
 * `nb_lignes` quand le back émettait `nb` : les deux côtés étaient justes,
 * c'est le CONTRAT qui était rompu, et aucun test ne pouvait le voir.
 * Ici la lecture d'un champ absent échoue BRUYAMMENT, à l'appel, pas en silence
 * trois écrans plus loin.
 */
/**
 * Ce que dit un code de statut quand le serveur n'a rien explique lui-meme.
 *
 * Les codes 502/503/504 ne viennent PAS de l'API : c'est le reverse-proxy qui
 * les emet quand il ne parvient pas a la joindre. Les nommer distinctement
 * evite de chercher un defaut applicatif la ou le probleme est un conteneur
 * arrete.
 */
function MessageDeStatut(statut: number): string {
  switch (statut) {
    case 502:
      return "API injoignable (502). Le serveur web repond, mais l'application derriere ne repond pas.";
    case 503:
      return "Service indisponible (503). L'application demarre peut-etre encore.";
    case 504:
      return "L'API n'a pas repondu a temps (504).";
    default:
      return `Erreur ${statut}`;
  }
}

export async function appeler<T>(
  chemin: string,
  schema: z.ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  // `init.signal` porte le signal d'abandon. Le transmettre n'est pas une
  // optimisation : sans lui, une requête remplacée par une plus récente
  // continue d'occuper le serveur pour un résultat que personne ne lira.
  const r = await fetch(`${BASE}${chemin}`, {
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...(jetonCourant ? { Authorization: `Bearer ${jetonCourant}` } : {}),
      ...init.headers,
    },
  });

  const texte = await r.text();

  // ⛔ LA LECTURE DU CORPS NE DOIT PAS MASQUER LE CODE DE STATUT.
  //
  // `JSON.parse` s'executait AVANT le test `r.ok`. Quand le reverse-proxy rend
  // sa propre page d'erreur — HTML, pas JSON — il levait, et l'ecran affichait
  // « Unexpected token '<', "<html>..." is not valid JSON ». Le 502 etait perdu
  // en chemin : le message accusait le format de la reponse au lieu de dire que
  // l'API etait injoignable, et on cherchait le defaut du mauvais cote.
  //
  // Un corps illisible est une information de MOINS, jamais une erreur de PLUS.
  let corps: unknown = null;
  try {
    corps = texte ? JSON.parse(texte) : null;
  } catch {
    if (r.ok) throw new ErreurApi(r.status, "Reponse illisible : le serveur n'a pas rendu du JSON.");
  }

  if (!r.ok) {
    // Le porteur d'un jeton refusé n'est plus connecté — sauf s'il n'avait
    // aucun jeton : un 401 sur un écran de connexion dit « identifiants
    // incorrects », pas « votre session a expiré ». Les confondre effacerait
    // une session valide chaque fois qu'un mot de passe est mal tapé.
    if (r.status === 401 && jetonCourant !== null) surExpiration?.();

    const c = corps as { erreur?: string; motif?: string } | null;
    throw new ErreurApi(
      r.status,
      c?.erreur ?? MessageDeStatut(r.status),
      c?.motif,
    );
  }

  const v = schema.safeParse(corps);
  if (!v.success) {
    // Rupture de contrat : on la nomme, on ne la dissout pas dans un `undefined`.
    throw new ErreurApi(
      r.status,
      `Contrat rompu sur ${chemin}`,
      v.error.issues.map((i) => `${i.path.join(".")} : ${i.message}`).join(" · "),
    );
  }
  return v.data;
}
